import type { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import type { AdvantagesBannerProps } from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import AdvantagesBanner from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import ArtistProfileList from '~/components/blocks/ArtistProfileList/ArtistProfileList'
import type { PeriodData } from '~/components/blocks/MAB100Chart/hooks'
import MAB100Chart from '~/components/blocks/MAB100Chart/MAB100Chart'
import MAB100CollectionTable from '~/components/blocks/MAB100CollectionTable/MAB100CollectionTable'
import RelatedArticles from '~/components/blocks/RelatedArticles/RelatedArticles'
import { resolveBlock } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'
import { useSettingsContext } from '~/contexts/settingsContext'
import { BannerTheme } from '~/enums/banner-theme'
import {
  advantagesBanner,
  articleCard,
  createBaseSelector,
  detectBodyModuleTypes,
  getBodyModules,
  myPortfolioBanner,
  notification,
  pageLanguageFilter,
} from '~/schemas/common'
import { getFAQData } from '~/schemas/JsonLd'
import {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { useSanity } from '~/services/sanity'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { getMAB100IndexFromRomeo } from '~/utils/getMAB100IndexFromRomeo'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { metaQuery } from '~/utils/seo'
import { resolvePageSeoMeta } from '~/utils/seoHelper'
import { urlQuery } from '~/utils/urlResolver'

import styles from '~/styles/pages.module.css'
import RecordTypes = indexes.RecordTypes
import { PushInteractionLocation } from '~/utils/posthogEvents'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

type MAB100Data = {
  series: any[]
  periods: PeriodData[]
  current: number
  change: string
  sanityArtworks: any[]
}

export interface PageData {
  pageSettings: {
    advantagesBanner: AdvantagesBannerProps
    tradingPageSummary: string
  }
  breadcrumb: BreadcrumbProps['list']
  mab100Data: MAB100Data
  body: any
}

const MAB100Page: NextPage<PageData> = ({
  pageSettings,
  breadcrumb,
  body,
  mab100Data,
}: PageData) => {
  const router = useRouter()
  const t = useTranslations('mab100')

  const { featuredArtists, totalArtists } = useSettingsContext()

  const pageBlocks = body?.body
  const relatedArticles = body?.relatedArticles

  return (
    <div>
      <Breadcrumb list={breadcrumb} />

      <MAB100Chart pageBlocks={pageBlocks} chartData={mab100Data.periods} />
      <MAB100CollectionTable items={mab100Data.sanityArtworks} />

      <ArtistProfileList
        title={t('mostInDemand')}
        artists={featuredArtists ? featuredArtists : []}
        totalArtists={totalArtists ? totalArtists : 0}
        theme={ComponentTheme.secondary}
        allBtnHandler={() => router.push('/artists')}
        verticalPadding
        searchInsightsEvent={{
          method: SearchAnalystMethods.clickedFilters,
          name: SearchAnalystEventNames.homeArtistClicked,
        }}
        recordType={RecordTypes.ARTIST}
        showViewAll={true}
      />
      {relatedArticles && relatedArticles.length > 0 && (
        <RelatedArticles articles={relatedArticles} />
      )}
      {pageBlocks && (
        <div className={styles.body}>
          {pageBlocks.map((block: any, i: any) => resolveBlock(block, i))}
        </div>
      )}
      <div id="sell-container">
        <AdvantagesBanner
          {...pageSettings.advantagesBanner}
          theme={BannerTheme.offwhite}
          analyticsInteractionLocation={PushInteractionLocation.Mab100Page}
          analyticsCtaSource="Mab100Page"
        />
      </div>
    </div>
  )
}

export const getStaticPropsOriginal: GetStaticProps =
  async function getStaticProps(context) {
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { client, limiter, scorer } = await useSanity(context)
      // Extract language from context for internationalized queries
      const lang = (context?.params?.lang as Locale) || 'en'
      const messages = await loadMessages(lang)

      // STEP 1: Pre-query to detect which body module types are actually used
      const baseSelector = createBaseSelector(
        'page',
        [`slug.current == $slug`, pageLanguageFilter(limiter)],
        limiter,
        scorer
      )
      const detectedTypes = await detectBodyModuleTypes(
        client,
        baseSelector,
        { slug: 'mab-100', lang },
        ['body']
      )

      // STEP 2: Main query with only the required body modules
      const { meta: metaProps, ...data } = await client.fetch(
        `{
        "body": *[_type == 'page' && slug.current == $slug && ${pageLanguageFilter(limiter)} ${limiter}] ${scorer}[0] {
          ${urlQuery},
          ${metaQuery('page')},
          redirectUrl,
          title,
          hasGoogleReviews,
          fullGoogleReviews,
          breadCrumbHidden,
          "body": body[] {
            ${getBodyModules(lang, detectedTypes)}
          },
          relatedArticlesTitle,
          "relatedArticles": relatedArticles[]-> {
            ${articleCard}
          },
          ${notification},
        },
        ${myPortfolioBanner}
        "meta": *[_type == 'page' && slug.current == $slug && ${pageLanguageFilter(limiter)} ${limiter}] ${scorer}[0]{ meta, _id },
        "pageSettings": *[_type == 'settings' ] [0] {
          ${advantagesBanner}
        }
      }`,
        { slug: 'mab-100', lang }
      )

      // ---- Retrieve romeo and sync to sanity ----
      const indexResponse = await getMAB100IndexFromRomeo(lang)

      // Use centralized SEO helper with locale-aware templates
      await resolvePageSeoMeta(context, metaProps, 'page')

      const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
        ['mab-100'],
        '',
        undefined,
        messages,
        lang
      )
      const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

      if (Object.keys(data).length < 1) {
        return {
          notFound: true,
        }
      }

      return {
        // revalidate: 21600, // Revalidates at most once every 6 hours (21600 seconds)
        props: {
          smallHeader: true,
          meta: metaProps.meta,
          url: '/mab-100',
          ...data,
          mab100Data: indexResponse.data,
          breadcrumb,
          breadcrumbJsonLd,
          hasGoogleReviews: data.body.hasGoogleReviews,
          noAnimatedTheme: true,
          darkFooter: true,
          theme: 'dark',
          jsonLd: {
            faqJsonLd: getFAQData(data.body?.body),
          },
        },
      }
    } catch {
      return {
        notFound: true,
      }
    }
  }

export function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

// Add SSR translation support
export const getStaticProps: GetStaticProps = withLocalizedSettings(
  async context => {
    const result = await getStaticPropsOriginal(context)

    if ('props' in result) {
      return {
        props: await addTranslations(result.props, context),
      }
    }

    return result
  }
)

export default MAB100Page
